// src/modules/app-views/account/pages/Init/modals/TeamInvitationModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { useState, useCallback, useMemo, useRef } from 'react';
import { UserAddOutlined, EditOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import accountService from 'modules/app-views/account/service';
import authService from 'services/authService';
import useZustand from 'hooks/useZustand';
import emailStep from './Step1';
import detailsStep from './Step2';
import rbacStep from './Step3';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TeamInvitationModal = ({
  show,
  setShow,
  afterClose,
  editRecord = null,
  // Team of the subscription being edited (not the active one from auth).
  // Falls back to auth.team for backwards compatibility with other callers.
  team = null,
  // Id of the subscription being edited — ensures team operations hit the right
  // subscription regardless of which one is active in the topbar selector.
  subscriptionId = null,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const authTeam = useZustand('auth', 'team');
  const storedTeam = team ?? authTeam;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Edit mode: when editRecord is provided
  const editMode = !!editRecord;
  const isEditingAccount = editMode && !!editRecord?.accountId;
  const isEditingInvitation = editMode && !!editRecord?.invitationId && !editRecord?.accountId;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [foundAccount, setFoundAccount] = useState(null);
  const [foundCollaborator, setFoundCollaborator] = useState(null);
  const [foundInvitation, setFoundInvitation] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Store initial values ref for comparison (only for edit mode)
  const initialValuesRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. LOCAL VARIABLES II (moved up for dependency order)
  // ─────────────────────────────────────────────────────────────────────────────
  // Initial values for the form (used for edit mode)
  const initialValues = useMemo(() => {
    if (!editMode) return {};

    const email = editRecord?.email?.value || editRecord?.email || '';
    const name = editRecord?.name || '';
    // Profile uses keyUser/guest internally (same as backend)
    const profile = editRecord?.profile || '';

    return { email, name, profile };
  }, [editMode, editRecord]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const resetFoundStates = useCallback(() => {
    setFoundAccount(null);
    setFoundCollaborator(null);
    setFoundInvitation(null);
  }, []);

  const handleFinish = useCallback(
    async ({ form }) => {
      const email = form?.getFieldValue('email');
      const name = form?.getFieldValue('name');
      const profile = form?.getFieldValue('profile'); // keyUser or guest

      try {
        if (editMode) {
          // UPDATE MODE
          if (isEditingAccount) {
            // Update an account's profile in subscription (name can't be changed for accounts)
            await accountService.changeUserProfile({
              userId: editRecord.accountId,
              profile,
              subscriptionId,
            });
          } else if (isEditingInvitation) {
            // Update invitation's name and profile
            await accountService.updateInvitation({
              _id: editRecord.invitationId,
              name,
              profile,
              subscriptionId,
            });
          }
        } else {
          // CREATE MODE
          await accountService.createUser(
            { email, name, profile },
            subscriptionId
          );
        }

        // await authService.signInRefresh();
      } catch (err) {
        console.error('[TeamInvitationModal] Error:', err);
        throw err;
      }
    },
    [editMode, isEditingAccount, isEditingInvitation, editRecord, subscriptionId]
  );

  const handleAfterClose = useCallback(() => {
    resetFoundStates();
    setHasChanges(false);
    initialValuesRef.current = null;
    afterClose?.();
  }, [afterClose, resetFoundStates]);

  // Handle form value changes to detect if user made any modifications
  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      if (!editMode) return;

      // Store initial values on first change if not yet stored
      if (!initialValuesRef.current) {
        initialValuesRef.current = { ...initialValues };
      }

      const initial = initialValuesRef.current;

      // Compare relevant fields (name and profile)
      const nameChanged = allValues.name !== initial.name;
      const profileChanged = allValues.profile !== initial.profile;

      setHasChanges(nameChanged || profileChanged);
    },
    [editMode, initialValues]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES III
  // ─────────────────────────────────────────────────────────────────────────────

  // Derived values based on found states or edit record
  // Name: account.name || collaborator.name || invitation.name
  const foundName = editMode
    ? editRecord?.name || ''
    : foundAccount?.name || foundCollaborator?.name || foundInvitation?.name || '';

  // Name is locked (disabled) only when editing an account (user already has an account)
  // or when a new user search returns an existing account
  const nameLocked = editMode ? isEditingAccount : !!foundAccount?.name;

  // Profile is SUBSCRIPTION-SPECIFIC. A user can have different profiles across
  // subscriptions, so never pre-fill nor lock based on a profile found in another
  // subscription's team. Profile must be chosen fresh by the admin.
  const foundProfile = null;
  const profileLocked = false;

  const steps = useMemo(
    () => [
      // Step 1: Email
      emailStep({
        storedTeam,
        setFoundAccount,
        setFoundCollaborator,
        setFoundInvitation,
        resetFoundStates,
      }),

      // Step 2: Details
      detailsStep({
        nameLocked,
        profileLocked,
        foundName,
        foundProfile,
        // Hide name field when editing a registered account (only profile is editable)
        hideName: isEditingAccount,
      }),

      // Step 3: RBAC (only for guest - hidden when keyUser is selected)
      rbacStep({ foundProfile }),
    ],
    [storedTeam, nameLocked, profileLocked, foundName, foundProfile, resetFoundStates, isEditingAccount]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={editMode ? 'edit_user' : 'new_user'}
      icon={editMode ? <EditOutlined /> : <UserAddOutlined />}
      steps={steps}
      onFinish={handleFinish}
      afterClose={handleAfterClose}
      width={480}
      initialValues={initialValues}
      initialStep={editMode ? 1 : 0} // Start at Step 2 in edit mode
      disableBack={editMode} // Can't go back to Step 1 in edit mode
      confirmDisabled={editMode && !hasChanges} // Disable confirm if no changes in edit mode
      onValuesChange={handleValuesChange}
      formProps={{
        initFocus: editMode ? (nameLocked ? 'profile' : 'name') : 'email',
      }}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TeamInvitationModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
